import { useEffect, useState } from 'react';
import api, { formatApiError, API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, FileCheck, AlertTriangle, ShieldCheck, Loader2, FileSignature, ArrowRight, CheckCircle2, FileText, XCircle, Clock as ClockIcon, RefreshCw } from 'lucide-react';

const initialForm = {
  date_of_birth: '', gender: '', marital_status: '',
  emergency_contact_name: '', emergency_contact_phone: '', address: '',
  aadhar_number: '', pan_number: '', aadhar_file_id: '', pan_file_id: '',
  undergrad_institute: '', undergrad_cert_file_id: '',
  postgrad_institute: '', postgrad_cert_file_id: '',
  previous_employers: [],
  certificate_file_ids: [],
  has_criminal_record: false, criminal_record_details: '',
  terms_accepted: false,
};

function FileUploader({ label, fileId, onUploaded, testId, required }) {
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(data.id, data.filename);
      setName(data.filename);
      toast.success(`${data.filename} uploaded`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  return (
    <div data-testid={testId}>
      <Label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-xl px-4 py-3 cursor-pointer hover:border-[#3D4A8E] hover:bg-slate-50 transition-colors">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> :
          fileId ? <FileCheck className="w-4 h-4 text-emerald-600" /> :
          <Upload className="w-4 h-4 text-slate-400" />}
        <span className="text-sm text-slate-600 truncate">{uploading ? 'Uploading…' : fileId ? (name || 'File uploaded') : 'Upload (JPG/PNG/PDF, ≤10MB)'}</span>
        <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
               onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
}

function inr(n) { if (n == null) return '—'; return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(user?.onboarding_status);
  const [busy, setBusy] = useState(false);
  const [offerLetter, setOfferLetter] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (user.onboarding_status === 'verified') { nav('/dashboard'); return; }
    if (user.role !== 'employee') { nav('/dashboard'); return; }
    (async () => {
      const { data } = await api.get('/onboarding/me');
      setStatus(data.status);
      if (data.form) setForm({ ...initialForm, ...data.form, previous_employers: data.form.previous_employers || [], certificate_file_ids: data.form.certificate_file_ids || [] });
    })();
  }, [user, nav]);

  // Load offer letter HTML for preview when status=offer_sent
  useEffect(() => {
    if (status !== 'offer_sent' || !user) return;
    (async () => {
      try { const { data } = await api.get(`/employees/${user.id}/offer-letter`); setOfferLetter(data); }
      catch (err) { console.debug('[offer-letter fetch]', err); }
    })();
  }, [status, user]);

  const acceptOffer = async () => {
    setBusy(true);
    try {
      await api.post('/onboarding/accept-offer');
      toast.success('Offer accepted! Please complete your background-check documents.');
      await refresh();
      setStatus('documents_pending');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadOffer = () => {
    if (!offerLetter) return;
    // Open via Blob URL (safer than document.write — no doc-rewrite needed).
    const url = URL.createObjectURL(new Blob([offerLetter.html], { type: 'text/html' }));
    const w = window.open(url, '_blank');
    if (!w) { URL.revokeObjectURL(url); toast.error('Pop-up blocked — allow pop-ups to view your offer letter'); return; }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/onboarding/submit', form);
      toast.success('Documents submitted — HR will run background verification');
      await refresh();
      setStatus('bgc_review');
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      toast.error(msg || 'Submission failed. Please check the highlighted errors below.');
    }
    finally { setBusy(false); }
  };

  const addEmployer = () => setForm({ ...form, previous_employers: [...form.previous_employers, { company_name: '', role: '', from_date: '', to_date: '', hr_name: '', hr_email: '', hr_phone: '' }] });
  const removeEmployer = (i) => setForm({ ...form, previous_employers: form.previous_employers.filter((_, idx) => idx !== i) });
  const setEmployer = (i, key, value) => {
    const arr = [...form.previous_employers];
    arr[i] = { ...arr[i], [key]: value };
    setForm({ ...form, previous_employers: arr });
  };

  if (!user) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const addCert = (id) => setForm({ ...form, certificate_file_ids: [...(form.certificate_file_ids || []), id] });
  const docsRequiredFilled = form.aadhar_file_id && form.pan_file_id &&
    form.undergrad_cert_file_id && form.aadhar_number && form.pan_number &&
    form.terms_accepted && (!form.has_criminal_record || (form.criminal_record_details || '').trim());

  // ============ STAGE 1: Offer pending ============
  if (status === 'offer_sent') {
    return (
      <div className="space-y-6" data-testid="onboarding-page">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Step 1 of 3</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Your offer from Lanvaus 🎉</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">Review the details below. Once you accept, you&apos;ll be asked to upload your background-check documents.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="offer-summary">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Offer summary</div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-2">{user.title || 'Team Member'}</h2>
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-600">
            <span>{user.department || '—'}</span>
            <span>·</span>
            <span>Grade {user.grade || '—'}</span>
            {user.seniority && <><span>·</span><span>{user.seniority}</span></>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              { label: 'Annual CTC', v: inr(user.annual_ctc) },
              { label: 'Monthly gross', v: inr(user.salary_structure?.monthly_gross) },
              { label: 'Monthly net', v: inr(user.salary_structure?.monthly_net), accent: 'text-emerald-700' },
              { label: 'Tax / month', v: inr(user.salary_structure?.monthly_tax), accent: 'text-red-600' },
            ].map((it, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{it.label}</div>
                <div className={`font-heading text-xl font-bold mt-1 ${it.accent || 'text-slate-900'}`}>{it.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={downloadOffer} variant="outline" data-testid="download-offer" className="rounded-full h-12 px-6">
            <FileSignature className="w-4 h-4 mr-2" /> Download offer letter
          </Button>
          <Button onClick={acceptOffer} disabled={busy} data-testid="accept-offer"
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white h-12 px-6">
            {busy ? 'Accepting…' : <>Accept & start onboarding <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    );
  }

  // ============ STAGE 3: Awaiting BGC or activation ============
  if (status === 'bgc_review' || status === 'bgc_cleared') {
    return <BgcStageView status={status} user={user} refresh={refresh} setStatus={setStatus} />;
  }

  // ============ STAGE 2: Documents (documents_pending or rejected) ============
  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Step 2 of 3</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Background-check documents</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">Upload the documents below for our background-check verification. All files are encrypted at rest and only visible to HR & Admin.</p>
      </div>

      {status === 'rejected' && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3" data-testid="status-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900">Documents returned — please update & resubmit</div>
            <div className="text-sm text-red-700 mt-0.5">HR has requested changes. Review and resubmit below.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="Personal">
          <Field label="Date of birth"><Input type="date" data-testid="ob-dob" value={form.date_of_birth} onChange={set('date_of_birth')} className="rounded-xl" /></Field>
          <Row>
            <Field label="Gender">
              <Select value={form.gender} onValueChange={set('gender')}>
                <SelectTrigger data-testid="ob-gender" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Marital status">
              <Select value={form.marital_status} onValueChange={set('marital_status')}>
                <SelectTrigger data-testid="ob-marital" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem><SelectItem value="married">Married</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Row>
          <Field label="Address"><Textarea rows={2} data-testid="ob-address" value={form.address} onChange={set('address')} className="rounded-xl" /></Field>
          <Row>
            <Field label="Emergency contact name"><Input data-testid="ob-emerg-name" value={form.emergency_contact_name} onChange={set('emergency_contact_name')} className="rounded-xl" /></Field>
            <Field label="Emergency contact phone"><Input data-testid="ob-emerg-phone" value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} className="rounded-xl" /></Field>
          </Row>
        </Section>

        <Section title="Identity">
          <Row>
            <Field label="Aadhar number *"><Input data-testid="ob-aadhar-num" value={form.aadhar_number} onChange={set('aadhar_number')} className="rounded-xl" /></Field>
            <Field label="PAN number *"><Input data-testid="ob-pan-num" value={form.pan_number} onChange={set('pan_number')} className="rounded-xl uppercase" /></Field>
          </Row>
          <FileUploader label="Aadhar card" required fileId={form.aadhar_file_id} testId="ob-aadhar-file"
                        onUploaded={(id) => setForm({ ...form, aadhar_file_id: id })} />
          <FileUploader label="PAN card" required fileId={form.pan_file_id} testId="ob-pan-file"
                        onUploaded={(id) => setForm({ ...form, pan_file_id: id })} />
          <div className="text-xs text-slate-500 mt-1 italic">Bank details will be collected after your background check is cleared.</div>
        </Section>

        <Section title="Education">
          <Row>
            <Field label="Undergrad institute"><Input data-testid="ob-ug-institute" value={form.undergrad_institute} onChange={set('undergrad_institute')} className="rounded-xl" /></Field>
            <Field label="Postgrad institute (if any)"><Input data-testid="ob-pg-institute" value={form.postgrad_institute} onChange={set('postgrad_institute')} className="rounded-xl" /></Field>
          </Row>
          <FileUploader label="Undergrad certificate" required fileId={form.undergrad_cert_file_id} testId="ob-ug-file"
                        onUploaded={(id) => setForm({ ...form, undergrad_cert_file_id: id })} />
          <FileUploader label="Postgrad certificate (optional)" fileId={form.postgrad_cert_file_id} testId="ob-pg-file"
                        onUploaded={(id) => setForm({ ...form, postgrad_cert_file_id: id })} />
        </Section>

        <Section title="Previous employers (optional — add multiple if applicable)" className="lg:col-span-2">
          {form.previous_employers.map((emp, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`prev-employer-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Employer #{i + 1}</div>
                <button type="button" onClick={() => removeEmployer(i)} data-testid={`remove-prev-emp-${i}`} className="text-xs text-red-500 hover:text-red-600">Remove</button>
              </div>
              <Row>
                <Field label="Company name"><Input data-testid={`prev-emp-name-${i}`} value={emp.company_name || ''} onChange={(e) => setEmployer(i, 'company_name', e.target.value)} className="rounded-xl" /></Field>
                <Field label="Role"><Input data-testid={`prev-emp-role-${i}`} value={emp.role || ''} onChange={(e) => setEmployer(i, 'role', e.target.value)} className="rounded-xl" /></Field>
              </Row>
              <Row>
                <Field label="From"><Input type="date" data-testid={`prev-emp-from-${i}`} value={emp.from_date || ''} onChange={(e) => setEmployer(i, 'from_date', e.target.value)} className="rounded-xl" /></Field>
                <Field label="To"><Input type="date" data-testid={`prev-emp-to-${i}`} value={emp.to_date || ''} onChange={(e) => setEmployer(i, 'to_date', e.target.value)} className="rounded-xl" /></Field>
              </Row>
              <Row>
                <Field label="HR / Manager name"><Input data-testid={`prev-emp-hrname-${i}`} value={emp.hr_name || ''} onChange={(e) => setEmployer(i, 'hr_name', e.target.value)} className="rounded-xl" /></Field>
                <Field label="HR / Manager email"><Input type="email" data-testid={`prev-emp-hremail-${i}`} value={emp.hr_email || ''} onChange={(e) => setEmployer(i, 'hr_email', e.target.value)} className="rounded-xl" /></Field>
              </Row>
              <Field label="HR / Manager phone"><Input data-testid={`prev-emp-hrphone-${i}`} value={emp.hr_phone || ''} onChange={(e) => setEmployer(i, 'hr_phone', e.target.value)} className="rounded-xl" /></Field>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addEmployer} data-testid="add-prev-employer" className="rounded-full">
            + Add previous employer
          </Button>
        </Section>

        <Section title="Other certifications (optional)" className="lg:col-span-2">
          <div className="space-y-2">
            {form.certificate_file_ids.map((id, i) => (
              <div key={i} className="text-xs text-slate-500 flex items-center gap-2"><FileCheck className="w-4 h-4 text-emerald-600" /> Certificate {i + 1} uploaded</div>
            ))}
            <FileUploader label="" fileId={null} testId="ob-cert-add" onUploaded={addCert} />
          </div>
        </Section>

        <Section title="Background check disclosure *" className="lg:col-span-2">
          <div className="flex items-start gap-3">
            <input type="checkbox" id="crim" data-testid="ob-has-criminal" checked={form.has_criminal_record} onChange={(e) => setForm({ ...form, has_criminal_record: e.target.checked })} className="mt-1" />
            <label htmlFor="crim" className="text-sm text-slate-700">I have a criminal record / pending legal case that I want to disclose.</label>
          </div>
          {form.has_criminal_record && (
            <Field label="Please describe (required) *">
              <Textarea data-testid="ob-criminal-details" rows={3} value={form.criminal_record_details} onChange={set('criminal_record_details')} className="rounded-xl" />
            </Field>
          )}
        </Section>

        <Section title="Terms & conditions *" className="lg:col-span-2">
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-600 leading-relaxed max-h-40 overflow-y-auto" data-testid="ob-terms-text">
            <p><b>Lanvaus Private Limited — Background Check Consent.</b></p>
            <p className="mt-2">By submitting this form, you confirm that the information provided is true, accurate, and complete to the best of your knowledge. You authorise Lanvaus Private Limited to verify the documents, references and disclosures submitted, including reaching out to previous employers, educational institutions and government databases for verification.</p>
            <p className="mt-2">Your data will be stored securely and used solely for employment, payroll and statutory compliance purposes. Documents are visible only to authorised HR personnel and administrators. You have the right to request a copy or deletion of your data subject to legal retention periods.</p>
            <p className="mt-2">Knowingly submitting false information is grounds for revoking the offer or terminating employment. You retain the right to withdraw your application before BGC clearance.</p>
          </div>
          <div className="flex items-start gap-3">
            <input type="checkbox" id="terms" data-testid="ob-terms-checkbox" checked={form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })} className="mt-1" />
            <label htmlFor="terms" className="text-sm text-slate-700">I have read and agree to the terms above. *</label>
          </div>
        </Section>
      </div>

      <div className="flex justify-end gap-3">
        <Button onClick={submit} disabled={busy || !docsRequiredFilled} data-testid="submit-onboarding"
                className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-8 h-12">
          {busy ? 'Submitting…' : (status === 'rejected' ? 'Resubmit documents' : 'Submit for BGC review')}
        </Button>
      </div>
    </div>
  );
}

const Section = ({ title, children, className = '' }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl p-6 space-y-4 ${className}`}>
    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{title}</div>
    {children}
  </div>
);
const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

function BgcStageView({ status, user, refresh, setStatus }) {
  const [bank, setBank] = useState({ bank_name: '', bank_account_number: '', bank_ifsc: '', account_holder_name: user?.name || '' });
  const [savedBank, setSavedBank] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/onboarding/me');
        if (data.form?.bank_name && data.form?.bank_account_number) {
          setSavedBank({
            bank_name: data.form.bank_name,
            bank_account_number: data.form.bank_account_number,
            bank_ifsc: data.form.bank_ifsc,
            account_holder_name: data.form.account_holder_name,
          });
        }
      } catch (err) { console.debug('[bank prefill]', err); }
    })();
  }, []);

  const submitBank = async () => {
    if (!bank.bank_name || !bank.bank_account_number || !bank.bank_ifsc) {
      return toast.error('Bank name, account number and IFSC are required');
    }
    setBusy(true);
    try {
      await api.post('/onboarding/bank-details', bank);
      toast.success('Bank details saved — used for payroll deposits');
      setSavedBank(bank);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Step 3 of 3</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Almost there!</h1>
      </div>
      {status === 'bgc_review' && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-3" data-testid="status-banner">
          <Loader2 className="w-5 h-5 text-amber-600 animate-spin mt-0.5" />
          <div>
            <div className="font-heading font-semibold text-amber-900">Background check in progress</div>
            <div className="text-sm text-amber-700 mt-1">HR has received your documents and is running the background verification. You&apos;ll receive an email once it&apos;s complete.</div>
          </div>
        </div>
      )}
      {status === 'bgc_cleared' && (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-start gap-3" data-testid="status-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-emerald-900">Background verification cleared 🎉</div>
              <div className="text-sm text-emerald-700 mt-1">We&apos;ve emailed your formal offer letter as a PDF. <b>Please sign it and upload the signed PDF below</b> for HR to review. While we wait, share your bank details so we can set up salary deposits.</div>
            </div>
          </div>

          <SignedOfferSection />

          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="bank-details-section">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Bank details for salary deposit</div>
            {savedBank ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="bank-details-saved">
                <div><div className="text-xs text-slate-500">Bank name</div><div className="font-medium">{savedBank.bank_name}</div></div>
                <div><div className="text-xs text-slate-500">Account number</div><div className="font-medium">{savedBank.bank_account_number}</div></div>
                <div><div className="text-xs text-slate-500">IFSC</div><div className="font-medium">{savedBank.bank_ifsc}</div></div>
                <div><div className="text-xs text-slate-500">Account holder</div><div className="font-medium">{savedBank.account_holder_name || '—'}</div></div>
                <div className="md:col-span-2"><Button variant="outline" onClick={() => setSavedBank(null)} className="rounded-full" data-testid="edit-bank-details">Edit bank details</Button></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Bank name *</Label>
                    <Input data-testid="bank-name" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Account holder name</Label>
                    <Input data-testid="bank-holder" value={bank.account_holder_name} onChange={(e) => setBank({ ...bank, account_holder_name: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Account number *</Label>
                    <Input data-testid="bank-account" value={bank.bank_account_number} onChange={(e) => setBank({ ...bank, bank_account_number: e.target.value.replace(/\s/g, '') })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">IFSC *</Label>
                    <Input data-testid="bank-ifsc" value={bank.bank_ifsc} onChange={(e) => setBank({ ...bank, bank_ifsc: e.target.value.toUpperCase() })} className="rounded-xl mt-1 uppercase" />
                  </div>
                </div>
                <Button onClick={submitBank} disabled={busy} data-testid="submit-bank" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white h-11 px-6">
                  {busy ? 'Saving…' : 'Save bank details'}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ============ Signed-offer upload (post-BGC) ============
function SignedOfferSection() {
  const [state, setState] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get('/onboarding/signed-offer/me');
      setState(data);
    } catch (e) { console.debug('[signed-offer me]', e); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file.'); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be 10 MB or smaller.'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post('/onboarding/signed-offer/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Signed offer uploaded — HR will review it shortly.');
      await load(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!state) return null;

  const banner = (() => {
    switch (state.status) {
      case 'pending_review':
        return { bg: 'bg-amber-50 border-amber-200', icon: <ClockIcon className="w-5 h-5 text-amber-600" />,
                 title: 'Signed offer received — awaiting HR review',
                 body: <>Uploaded on <b>{state.uploaded_at?.slice(0, 16).replace('T', ' ')}</b>. We&apos;ll email you the outcome.</> };
      case 'approved':
        return { bg: 'bg-emerald-50 border-emerald-200', icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
                 title: 'Signed offer approved 🎉',
                 body: <>You&apos;re officially confirmed. HR will activate your work email shortly.</> };
      case 'reupload_required':
        return { bg: 'bg-orange-50 border-orange-200', icon: <RefreshCw className="w-5 h-5 text-orange-600" />,
                 title: 'HR asked you to re-upload',
                 body: state.review_note ? <>Reason: <b>{state.review_note}</b></> : <>Please upload an updated signed PDF.</> };
      case 'rejected':
        return { bg: 'bg-red-50 border-red-200', icon: <XCircle className="w-5 h-5 text-red-600" />,
                 title: 'Signed offer rejected by HR',
                 body: <>Reason: <b>{state.review_note || '—'}</b><br/>Please contact HR for next steps.</> };
      default:
        return null;
    }
  })();

  const canUpload = state.status === 'not_uploaded' || state.status === 'reupload_required';

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="signed-offer-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#3D4A8E]" />
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Upload signed offer letter</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => load()} disabled={refreshing}
                className="text-xs text-slate-500 hover:text-slate-700" data-testid="refresh-signed-offer">
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {banner && (
        <div className={`border rounded-xl p-4 flex items-start gap-3 ${banner.bg}`} data-testid={`signed-offer-banner-${state.status}`}>
          {banner.icon}
          <div className="text-sm">
            <div className="font-semibold text-slate-900">{banner.title}</div>
            <div className="text-slate-700 mt-0.5">{banner.body}</div>
          </div>
        </div>
      )}

      <div className="text-sm text-slate-600">
        After signing the offer letter PDF we emailed you (digitally or by printing &amp; scanning), upload the signed copy here. <b>PDF only · max 10 MB.</b>
      </div>

      {canUpload && (
        <label className="flex items-center gap-3 border border-dashed border-slate-300 rounded-xl px-4 py-4 cursor-pointer hover:border-[#3D4A8E] hover:bg-slate-50 transition-colors"
               data-testid="signed-offer-uploader">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Upload className="w-5 h-5 text-[#3D4A8E]" />}
          <span className="text-sm font-medium text-slate-700">
            {uploading ? 'Uploading…' : (state.status === 'reupload_required' ? 'Click to upload a new signed PDF' : 'Click to upload your signed offer PDF')}
          </span>
          <input type="file" accept="application/pdf,.pdf" className="hidden"
                 onChange={(e) => upload(e.target.files?.[0])}
                 data-testid="signed-offer-input" />
        </label>
      )}

      {state.history && state.history.length > 0 && (
        <details className="text-xs text-slate-500" data-testid="signed-offer-history">
          <summary className="cursor-pointer hover:text-slate-700">History ({state.history.length})</summary>
          <ul className="mt-2 space-y-1.5 ml-4">
            {state.history.map((h, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-3">
                <b className="text-slate-700">{h.action}</b>
                {' · '}<span>{h.at?.slice(0, 16).replace('T', ' ')}</span>
                {h.by_name && <> · by <span className="text-slate-700">{h.by_name}</span></>}
                {h.note && <div className="italic">Note: {h.note}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
